import React, { useEffect, useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    Grid,
    TextField,
    Typography,
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import AppDialogTitle from '../../../../ui/appDialogTitle';
import { ValidDataService } from '../../../../../service/validDataService';

const parseMultiValue = (value) => String(value || '')
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);

const normalizeMultiValues = (values = []) => {
    const uniqueValues = new Map();
    values.forEach((value) => {
        const normalizedValue = String(value || '').trim();
        if (!normalizedValue) return;
        const key = normalizedValue.toLowerCase();
        if (!uniqueValues.has(key)) uniqueValues.set(key, normalizedValue);
    });
    return [...uniqueValues.values()];
};

const serializeMultiValue = (values) => normalizeMultiValues(values).join(', ');

const getFieldError = (field, value) => {
    if (!field.maxLength || String(value || '').length <= field.maxLength) return '';
    return `${field.label} cannot exceed ${field.maxLength} characters`;
};

const hasFieldErrors = (eyeState, fields) => fields.some((field) => (
    Boolean(getFieldError(field, eyeState.manualContactLens?.[field.key]))
));

const buildDefaultEyeState = () => ({
    price: '',
    quantity: 1,
    manualContactLens: {}
});

const hasEyeInput = (eyeState = {}, fields = []) => {
    if (!eyeState) return false;

    const hasPrice = eyeState.price !== '' && eyeState.price !== null && eyeState.price !== undefined;
    const hasQuantity = Number(eyeState.quantity || 1) !== 1;
    const hasManualFields = fields.some((field) => {
        const value = eyeState.manualContactLens?.[field.key];
        return value !== '' && value !== null && value !== undefined;
    });

    return hasPrice || hasQuantity || hasManualFields;
};

const normalizeFieldValue = (type, value) => {
    if (value === '' || value === null || value === undefined) return '';
    if (type === 'integer') {
        const next = Number.parseInt(value, 10);
        return Number.isNaN(next) ? '' : next;
    }
    if (type === 'number') {
        const next = Number(value);
        return Number.isNaN(next) ? '' : next;
    }
    return typeof value === 'string' ? value.trim() : value;
};

const cleanManualContactLensPayload = (manualContactLens = {}, fields = []) => {
    const payload = {};

    fields.forEach((field) => {
        const value = manualContactLens[field.key];
        if (value === undefined || value === null || value === '') return;
        payload[field.key] = normalizeFieldValue(field.type, value);
    });

    return payload;
};

const buildManualContactLensDescription = (eye, manualContactLens = {}) => {
    const descriptor = [
        ['Qty (per box)', manualContactLens.lensPerBox],
        ['Manufacturer', manualContactLens.manufacturerName],
        ['Brand', manualContactLens.brandName],
        ['Model', manualContactLens.modelName],
        ['Material', manualContactLens.materialName],
        ['Schedule', manualContactLens.scheduleName],
    ]
        .filter(([, value]) => value !== undefined && value !== null && value !== '')
        .map(([label, value]) => `${label}: ${value}`)
        .join(' | ');

    return descriptor ? `CL (${eye}): ${descriptor}` : `CL (${eye})`;
};

export default function ManualContactLensDialog({
    open,
    onClose,
    onSelect,
    existingManualContactLensData = null,
}) {
    const [schema, setSchema] = useState(null);
    const [od, setOd] = useState(buildDefaultEyeState());
    const [os, setOs] = useState(buildDefaultEyeState());

    const fields = useMemo(() => schema?.fields || [], [schema]);

    useEffect(() => {
        if (!open) return;

        const loadSchema = async () => {
            const response = await ValidDataService.getManualContactLensSchema();
            const schemaData = response?.data || response;
            setSchema(schemaData || null);
        };

        loadSchema();
    }, [open]);

    useEffect(() => {
        if (!open) return;

        if (!existingManualContactLensData) {
            setOd(buildDefaultEyeState());
            setOs(buildDefaultEyeState());
            return;
        }

        const odItem = existingManualContactLensData.odContactLens || null;
        const osItem = existingManualContactLensData.osContactLens || null;

        setOd({
            price: odItem?.price ?? '',
            quantity: odItem?.quantity ?? 1,
            manualContactLens: {
                ...(odItem?.itemConfig?.manualContactLens || {})
            }
        });

        setOs({
            price: osItem?.price ?? '',
            quantity: osItem?.quantity ?? 1,
            manualContactLens: {
                ...(osItem?.itemConfig?.manualContactLens || {})
            }
        });
    }, [open, existingManualContactLensData]);

    const handleEyeMetaChange = (eye, key, value) => {
        if (eye === 'OD') {
            setOd((prev) => ({ ...prev, [key]: value }));
            return;
        }
        setOs((prev) => ({ ...prev, [key]: value }));
    };

    const handleFieldChange = (eye, key, value) => {
        if (eye === 'OD') {
            setOd((prev) => ({ ...prev, manualContactLens: { ...prev.manualContactLens, [key]: value } }));
            return;
        }
        setOs((prev) => ({ ...prev, manualContactLens: { ...prev.manualContactLens, [key]: value } }));
    };

    const copyEye = (fromEye, toEye) => {
        const source = fromEye === 'OD' ? od : os;

        if (toEye === 'OD') {
            setOd({ ...source });
            return;
        }
        setOs({ ...source });
    };

    const buildItem = (eye, eyeState, groupId, existingItem = null) => {
        if (!hasEyeInput(eyeState, fields)) return null;

        const manualContactLensPayload = cleanManualContactLensPayload(eyeState.manualContactLens, fields);
        return {
            ...(existingItem || {}),
            id: existingItem?.id || uuidv4(),
            type: 'contact_lens',
            eye,
            itemId: null,
            groupId,
            quantity: Number(eyeState.quantity || 1),
            price: Number(eyeState.price || 0),
            tax: existingItem?.tax ?? 0,
            discount: existingItem?.discount ?? 0,
            total: 0,
            date: existingItem?.date || new Date().toISOString(),
            description: buildManualContactLensDescription(eye, manualContactLensPayload),
            itemConfig: {
                manualContactLens: manualContactLensPayload,
                manualEntry: true,
            }
        };
    };

    const handleSave = () => {
        if (!hasEyeInput(od, fields) && !hasEyeInput(os, fields)) {
            return;
        }
        if (hasFieldErrors(od, fields) || hasFieldErrors(os, fields)) {
            return;
        }

        const groupId = existingManualContactLensData?.groupId || uuidv4();

        const payload = {
            groupId,
            odContactLens: buildItem('OD', od, groupId, existingManualContactLensData?.odContactLens || null),
            osContactLens: buildItem('OS', os, groupId, existingManualContactLensData?.osContactLens || null),
        };

        onSelect(payload);
    };

    const renderEyeSection = (eye, eyeState) => (
        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{eye}</Typography>
                <Box>
                    {eye === 'OD' && <Button size="small" onClick={() => copyEye('OD', 'OS')}>Copy to OS</Button>}
                    {eye === 'OS' && <Button size="small" onClick={() => copyEye('OS', 'OD')}>Copy to OD</Button>}
                </Box>
            </Box>

            <Grid container spacing={1}>
                <Grid item xs={12} md={6}>
                    <TextField
                        size="small"
                        fullWidth
                        label="Price"
                        value={eyeState.price}
                        onChange={(e) => handleEyeMetaChange(eye, 'price', e.target.value)}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        size="small"
                        fullWidth
                        label="Quantity (Number of boxes)"
                        type="number"
                        value={eyeState.quantity}
                        onChange={(e) => handleEyeMetaChange(eye, 'quantity', e.target.value)}
                    />
                </Grid>
                {fields.map((field) => {
                    const currentValue = eyeState.manualContactLens[field.key] ?? '';
                    const fieldError = getFieldError(field, currentValue);
                    const commonProps = {
                        key: `${eye}-${field.key}`,
                    };

                    if (field.control === 'autocomplete') {
                        const isMultiple = Boolean(field.multiple);
                        const options = field.options || [];
                        const freeSolo = field.allowCustom !== false;
                        const value = isMultiple
                            ? parseMultiValue(currentValue)
                            : (currentValue || null);

                        return (
                            <Grid item xs={12} md={6} {...commonProps}>
                                <Autocomplete
                                    size="small"
                                    options={options}
                                    freeSolo={freeSolo}
                                    multiple={isMultiple}
                                    limitTags={field.limitTags || 2}
                                    value={value}
                                    onChange={(_, nextValue) => {
                                        if (isMultiple) {
                                            handleFieldChange(eye, field.key, serializeMultiValue(nextValue || []));
                                            return;
                                        }
                                        handleFieldChange(eye, field.key, (nextValue || '').toString());
                                    }}
                                    onInputChange={(_, inputValue, reason) => {
                                        if (isMultiple || reason !== 'input') return;
                                        handleFieldChange(eye, field.key, inputValue);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={field.label}
                                            error={Boolean(fieldError)}
                                            helperText={fieldError}
                                            multiline={Boolean(field.multiline)}
                                            minRows={field.multiline ? 3 : undefined}
                                        />
                                    )}
                                />
                            </Grid>
                        );
                    }

                    return (
                        <Grid item xs={12} md={6} {...commonProps}>
                            <TextField
                                size="small"
                                fullWidth
                                type={field.type === 'number' || field.type === 'integer' ? 'number' : 'text'}
                                label={field.label}
                                value={currentValue}
                                onChange={(event) => handleFieldChange(eye, field.key, event.target.value)}
                                error={Boolean(fieldError)}
                                helperText={fieldError}
                                multiline={Boolean(field.multiline)}
                                minRows={field.multiline ? 3 : undefined}
                            />
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <AppDialogTitle title="Manual Contact Lens Entry" onClose={onClose} />
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        {renderEyeSection('OD', od)}
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {renderEyeSection('OS', os)}
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={
                        (!hasEyeInput(od, fields) && !hasEyeInput(os, fields))
                        || hasFieldErrors(od, fields)
                        || hasFieldErrors(os, fields)
                    }
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}
